import { Button, Card, Stack, Typography } from "@mui/material";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CircularIndeterminate } from "../../../components/common/CircularIndeterminate";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { setProcessingRequest } from "../../../store/reducers/tableManagementReducer";
import { useProcessingRequest } from "../../../store/selectors/tableManagementSelector";
import { ExcelLink, TemplateDownload } from "../../actionModules/ActionsBlock.styles";
import ActionModuleIncentivation from "./incentivationTable/ActionModuleIncentivation";
import { GridOn } from "@mui/icons-material";
import ExcelUploadButton from "./ExcelUploadButton";
import ScoreRanges from "../../actionModules/ActionsBlock/ActionCard/ExcelIncentivation/ScoreRanges";
import DefaultIcon from "../../../components/common/DefaultIcon";
import { actionModulesApi } from "../../../api/actionsModulesApi";
import { ActionModule } from "../../../types/actionModule/actionModule";
import { IncentivationSourceType, ManualActionIncentivationSourceModel } from "../../../types/actionModule/incentivationSourceModel";
import { RewardCalculationType, ScoreRangeRewardCalculationType } from "../../../types/actionModule/rewardCalculationType";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { PermissionEntityType } from "../../../types/permissionType";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { uploadIncentivationExcelThunk } from "../../../store/thunk/incentivationThunk";
import { CallResult } from "../../../api/Requests";
import { bookTransactionForActionModuleThunk } from "../../../store/thunk/actionsModulesThunk";
import { TransactionIcon } from "../../../assets/icons/Icons";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { EmployeeIncentivation } from "../../../types/employeeScores";
import dayjs, { Dayjs } from "dayjs";

export default function ActionModuleIncentivationPage() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { actionModuleId } = useParams();
    const processingRequest = useProcessingRequest();

    const [actionModule, setActionModule] = useState<ActionModule | null>(null);
    const [confirmTransactionOpen, setConfirmTransactionOpen] = useState(false);

    const defaultDateRange = useMemo(() => ({
        startDate: dayjs().startOf('week'),
        endDate: dayjs().endOf('week'),
    }), [""]);
    const [dateRange, setDateRange] = useState<{ startDate: Dayjs, endDate: Dayjs }>(defaultDateRange);

    const handleConfirmTransaction = async () => {
        if (!actionModuleId) return;
        await dispatch(bookTransactionForActionModuleThunk({ actionModuleId }));
        setConfirmTransactionOpen(false);
    };

    const { userHasPermission } = usePermissionChecker();
    const canIncentivate = userHasPermission(PermissionEntityType.ActionModules, "Incentivate")
        || userHasPermission(PermissionEntityType.ActionModules, "Incentivate", actionModule?.id);

    useEffect(() => {
        const fetchActionModule = async () => {
            if (!actionModuleId) return;
            dispatch(setProcessingRequest(true));
            const res = await actionModulesApi.getAction(actionModuleId);
            dispatch(setProcessingRequest(false));
            setActionModule(res?.result ?? null);
        };
        fetchActionModule();
    }, [actionModuleId]);

    const fetchPaginatedEmployees = useCallback(async (paginationParameters) => {
        if (!actionModule) return { items: [], totalCount: 0 };
        const res = await actionModulesApi.getEmployeesForActionModule(
            actionModule.id,
            paginationParameters,
            dateRange.startDate,
            dateRange.endDate
        );
        return res?.result;
    }, [actionModule, dateRange]);

    const paginatedData = useServerSidePagination(
        fetchPaginatedEmployees,
        [actionModule?.id, dateRange]
    );

    const breadcrumbs = [
        ...actionModule ? [{
            title: actionModule.name,
            link: `incentivation/actionModules/${actionModuleId}`,
        }] : [],
    ];

    const canExcelUpload =
        actionModule && canIncentivate &&
        actionModule.incentivationSource?.type == IncentivationSourceType.ManualActionIncentivationSource &&
        (actionModule.incentivationSource as ManualActionIncentivationSourceModel).canBulkUploadIncentivationData;

    const scoreRanges = (actionModule &&
        actionModule.rewardCalculationType?.type === RewardCalculationType.ScoreRangeCalculationType)
        ? (actionModule.rewardCalculationType as ScoreRangeRewardCalculationType).scoreRanges
        : null;

    return (
        <>
            <PageTitle
                breadcrumbs={breadcrumbs}
                title={t("incentivationPage.title")}
            />
            {actionModule && (
                <Card sx={{ mb: 5, p: 3, minHeight: "150px" }}>
                    <Stack direction="column">
                        <Stack direction="row" gap={2} alignItems="center" mb={3}>
                            <DefaultIcon
                                iconName={actionModule.iconName}
                                colorHex={actionModule.colorHex ?? ""}
                                iconUrl={actionModule.iconUrl}
                            />
                            <Typography variant="h5" sx={{ flex: 1 }}>
                                {actionModule.name}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setConfirmTransactionOpen(true)}
                                sx={{
                                    alignSelf: "center",
                                    color: "white",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 1,
                                    whiteSpace: "normal",
                                    textAlign: "left",
                                    px: 2,
                                    minWidth: 160,
                                }}
                            >
                                <TransactionIcon colorHex="#fff" />
                                {t("buttons.bookTransaction")}
                            </Button>
                        </Stack>

                        <Stack direction="row" gap={2}>
                            {canExcelUpload && canIncentivate && (
                                <ExcelUploadArea
                                    actionModule={actionModule}
                                    startDate={dateRange.startDate}
                                    endDate={dateRange.endDate}
                                    onUploadSuccess={() => paginatedData?.updateTableItems()}
                                />
                            )}
                            {scoreRanges && <ScoreRanges scoreRanges={scoreRanges} />}
                        </Stack>
                    </Stack>
                </Card>
            )}

            <Card>
                {actionModule && paginatedData &&
                    <ActionModuleIncentivation
                        actionModule={actionModule}
                        paginatedData={paginatedData}
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        onResetDateRange={() => setDateRange(defaultDateRange)}
                        onDateRangeChange={(startDate, endDate) => setDateRange({
                            startDate,
                            endDate
                        })} />
                }
            </Card>
            <CircularIndeterminate processingRequest={processingRequest} />

            <ConfirmDialog
                open={confirmTransactionOpen}
                title={t("buttons.bookTransaction")}
                message={t("dialogModals.confirmTransactionActionModule")}
                onClose={() => setConfirmTransactionOpen(false)}
                onConfirm={handleConfirmTransaction}
            />
        </>
    );
}

interface ExcelUploadAreaProps {
    actionModule: ActionModule;
    startDate: Dayjs;
    endDate: Dayjs;
    onUploadSuccess?: () => void;
}

function ExcelUploadArea(props: ExcelUploadAreaProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const handleExcelUpload = useCallback(async (data: EmployeeIncentivation[]) => {
        if (!props.actionModule) return;
        const result = await dispatch(
            uploadIncentivationExcelThunk({
                actionModuleId: props.actionModule.id,
                employeeScores: data,
                startDate: props.startDate,
                endDate: props.endDate,
            })
        );

        if ((result.payload as CallResult<unknown>)?.success() && props.onUploadSuccess) {
            props.onUploadSuccess();
        }
    }, [props.actionModule, props.onUploadSuccess]);

    return <Stack direction="column" gap={2}>
        <TemplateDownload>
            <GridOn sx={{ fontSize: "2rem", paddingRight: "0.25em" }} />
            <Typography>
                <ExcelLink href="/files/templates/EmployeeNumber_Score.xlsx">{t("buttons.templateDownload")}</ExcelLink>
            </Typography>
        </TemplateDownload>
        <ExcelUploadButton onUpload={handleExcelUpload} actionModule={props.actionModule} />
    </Stack>
}